export const getTranslations = (language) => {
  const translations = {
    english: {
      // Header
      informationForm: "Patient Information",
      formDescription:
        "Please fill out all required information accurately. This form is used to collect essential patient information for your SIBO breath test analysis.",

      // Test Information
      testInformation: "Test Information",
      orderNumber: "Order Number",
      testIdentifier: "Test Identifier",
      typeOfTest: "Type of Test",
      testSubstrate: "Test Substrate",

      // Form Status
      formNotEditable: "This form is not editable currently.",
      loadingFormData: "Loading form data...",

      // Mandatory Information
      mandatoryInformation: "Mandatory Information",
      firstName: "First Name",
      lastName: "Last Name",
      dateOfBirth: "Date of Birth",
      enterFirstName: "Enter first name",
      enterLastName: "Enter last name",

      // Additional Email
      additionalEmailAddress: "Additional email address",
      additionalEmailDescription:
        "Optionally add one or more email addresses to receive a copy of the results.",
      enterAdditionalEmail: "Enter additional email address",
      addEmailAddress: "Add Email Address",
      removeEmail: "Remove",

      // Additional Information
      additionalInformation: "Additional Information",
      initialOrFollowUp: "Initial or Follow-up Test",
      initialFindings: "Initial test",
      followUpTest: "Follow-up test",

      // Daily Symptoms
      dailySymptoms: "Daily Symptoms",
      selectDailySymptoms: "Select your daily symptoms:",
      constipation: "Constipation",
      bloating: "Bloating",
      diarrhoea: "Diarrhoea",
      abdominalPain: "Abdominal Pain",
      mixed: "Mixed",
      flatulence: "Flatulence",

      // Physician or Therapist
      physicianTherapist: "Physician or Therapist",
      therapistSelectionDescription:
        "Select your therapist or doctor from the list so that an additional copy of your results is sent to them. Only practices registered with Sibolab are listed. If your practice is not in the list, please enter the details manually under 'Other'.",
      therapistTestNumberNote:
        "Please make sure to provide your therapist with your test number so they can access and download your test report.",
      searchTherapist: "Search Therapist",
      searchTherapistHint: "Type therapist name, practice name, or city...",
      therapistName: "Therapist Name",
      practiceName: "Practice Name",
      city: "City",
      email: "Email",
      entertherapistName: "Enter Therapist Name",
      enterpracticeName: "Enter Practice Name",
      entercity: "Enter City",
      enteremail: "Enter Email",

      otherNotListed: "Other (not listed)",
      enterCustomTherapist: "Enter custom therapist information",
      customTherapistInformation: "Custom Therapist Information",
      customTherapistInfoDescription:
        "Please enter the details of your therapist or doctor.",

      // Test Symptoms
      testSymptoms: "Symptoms During Test Performance",
      testSymptomsDescription:
        "Please choose the symptoms that occurred during your test and mention the time in minutes when they appeared.",
      minutes: "Minutes",
      others: "Others",
      addSymptom: "Add Symptom",
      removeSymptom: "Remove",
      describeOtherSymptoms: "Describe other symptoms (2-3) words",
      wordsUsed: "words",
      maximumSymptoms: "(Maximum 3 Symptoms)",
      keepbrief: "Please keep it brief.",
      toomanywords: "Too many words",
      maximumSymptomsReached: "Maximum 3 symptoms allowed",

      // Declaration
      declaration: "Declaration",
      consentDeclaration:
        "I hereby declare my consent to the evaluation of my breath test samples by Sibolab. I have taken note of the instructions for test preparation and execution.",
      dataUsageAgreement:
        "I agree that my health data may be used anonymously and pseudonymously to conduct research and development for new services and scientific studies (voluntary).",

      // Buttons
      save: "Save",
      submit: "Submit",
      cancel: "Cancel",
      confirm: "Confirm",
      close: "Close",
      editSubmission: "Edit Submission",

      // Messages
      thankYou: "Thank You!",
      loadingTestInformation: "Loading test information...",
      formSavedSuccessfully: "Form saved successfully!",
      formSubmittedSuccessfully: "Form submitted successfully!",
      errorSavingForm: "Error saving form. Please try again.",
      errorSubmittingForm: "Error submitting form. Please try again.",
      draftLoaded: "Draft loaded from",
      formDataLoaded: "Form data loaded successfully!",
      errorLoadingForm: "Error loading form data. Please try again.",
      loading: "(Loading...)",
      searchMoreRecords: "(Searching more records...)",
      loadingTherapists: "Loading therapists...",
      searchAllTherapists: "Searching all therapist records...",
      loadingTherapistsSecondary:
        "Please wait while we fetch the therapist list...",

      // Validation
      pleaseEnterFirstName: "Please enter first name",
      pleaseEnterLastName: "Please enter last name",
      pleaseEnterDateOfBirth: "Please enter date of birth",
      pleaseSelectTherapist: "Please select a therapist",
      pleaseAcceptConsent: "Please accept the consent declaration",
      pleaseAcceptDataUsage: "Please accept the data usage agreement",
      pleaseFillRequiredFields: "Please fill all required fields before submitting.",

      // Instructions Popup
      instructionsTitle: "Instructions",
      instructionsContent: "Instructions will be added here.",
      download: "Download",
      selectLanguage: "Select Language",
      understood: "Understood",
      sampleCollectionVideoTitle: "Video: Sample Collection",
      // Labels for the video player controls (Plyr `i18n` option)
      sampleCollectionVideoPlayer: {
        play: "Play",
        pause: "Pause",
        rewind: "Rewind {seektime}s",
        fastForward: "Forward {seektime}s",
        seek: "Seek",
        seekLabel: "{currentTime} of {duration}",
        currentTime: "Current time",
        settings: "Settings",
        speed: "Speed",
        normal: "Normal",
        enterFullscreen: "Enter fullscreen",
        exitFullscreen: "Exit fullscreen",
        menuBack: "Go back to previous menu",
      },

      // Dialog Messages
      saveDialogTitle: "Save Form",
      saveDialogMessage: "Are you sure you want to save the form as draft?",
      submitDialogTitle: "Submit Form",
      submitInstructions:
        "Your data is transmitted to us and stored securely, and is not shared with any unauthorized third party, in compliance to relevant German and EU data privacy laws.",
      submitDialogMessage:
        "Are you sure you want to submit the form? This action cannot be undone.",
    },

    german: {
      // Header
      informationForm: "Patientenauskunft",
      formDescription:
        "Bitte füllen Sie alle erforderlichen Informationen sorgfältig und korrekt aus. Dieses Formular dient der Erfassung wichtiger Patientendaten für Ihre SIBO-Atemtestanalyse.",

      // Test Information
      testInformation: "Testinformationen",
      orderNumber: "Auftragsnummer",
      testIdentifier: "Test ID",
      typeOfTest: "Testart",
      testSubstrate: "Testsubstrat",

      // Form Status
      formNotEditable: "Dieses Formular ist derzeit nicht bearbeitbar.",
      loadingFormData: "Formulardaten werden geladen...",

      // Mandatory Information
      mandatoryInformation: "Pflichtangaben",
      firstName: "Vorname",
      lastName: "Name",
      dateOfBirth: "Geburtsdatum",
      enterFirstName: "Vorname eingeben",
      enterLastName: "Name eingeben",

      // Additional Email
      additionalEmailAddress: "Zusätzliche E-Mail-Adresse",
      additionalEmailDescription:
        "Optional können Sie eine oder mehrere E-Mail-Adressen angeben, an die eine Kopie der Ergebnisse gesendet wird.",
      enterAdditionalEmail: "Zusätzliche E-Mail-Adresse eingeben",
      addEmailAddress: "E-Mail-Adresse hinzufügen",
      removeEmail: "Entfernen",

      // Additional Information
      additionalInformation: "Zusätzliche Informationen",
      initialOrFollowUp: "Erst-oder Folgetest",
      initialFindings: "Ersttest",
      followUpTest: "Folgetest",

      // Daily Symptoms
      dailySymptoms: "Beschwerden im Alltag",
      selectDailySymptoms: "Wählen Sie Ihre täglichen Symptome aus:",
      constipation: "Obstipation",
      bloating: "Blähbauch",
      diarrhoea: "Diarrhö",
      abdominalPain: "Abdominale Schmerzen",
      mixed: "Obstipation/Diarrhö im Wechsel",
      flatulence: "Blähungen",

      // Physician or Therapist
      physicianTherapist: " Behandelnde(r) Ärzt*in bzw. Therapeut*in",
      therapistSelectionDescription:
        "Wählen Sie Ihre:n Therapeut:in oder Ärzt:in aus der Liste aus, damit eine zusätzliche Ergebniszusendung erfolgt. In der Liste sind ausschließlich bei Sibolab registrierte Praxen aufgeführt. Falls Ihre Praxis nicht in der Liste enthalten ist, geben Sie die Angaben bitte unter \u201eSonstige\u201c manuell ein.",
      therapistTestNumberNote:
        "Bitte teilen Sie Ihrer Therapeutin bzw. Ihrem Therapeuten Ihre Testnummer mit, damit sie bzw. er Ihren Testbericht abrufen und herunterladen kann.",
      searchTherapist: "Therapeut*in suchen",
      searchTherapistHint: "Name, Praxis oder Stadt eingeben...",
      therapistName: "Name des Therapeuten",
      practiceName: "Praxisname",
      city: "Stadt",
      email: "E-Mail",
      entertherapistName: "Name des Therapeuten eingeben",
      enterpracticeName: "Praxisnamen eingeben",
      entercity: "Stadt eingeben",
      enteremail: "E-Mail eingeben",

      otherNotListed: "Sonstige (nicht aufgeführt)",
      enterCustomTherapist:
        "Benutzerdefinierte Therapeuteninformationen eingeben",
      customTherapistInformation:
        "Benutzerdefinierte Therapeuteninformationen",
      customTherapistInfoDescription:
        "Bitte geben Sie Angaben zu Ihre:m Therapeut*in oder Ärzt*in ein.",

      // Test Symptoms
      testSymptoms: "Symptomen während der Testdurchführung",
      testSymptomsDescription:
        "Bitte wählen Sie die Symptome aus, die während des Tests aufgetreten sind und geben Sie die jeweilige Minute ihres Auftretens an.",
      minutes: "Minuten",
      others: "Sonstige",
      addSymptom: "Symptom hinzufügen",
      removeSymptom: "Entfernen",
      describeOtherSymptoms: "Sonstige Symptome beschreiben (2–3 Wörter)",
      wordsUsed: "Wörter verwendet",
      maximumSymptoms: "(Maximal 3 Symptome)",
      keepbrief: "Bitte kurz halten.",
      toomanywords: "Zu viele Wörter",
      maximumSymptomsReached: "Maximal 3 Symptome erlaubt",

      // Declaration
      declaration: "Erklärung",
      consentDeclaration:
        "Hiermit erkläre ich mein Einverständnis zu der Auswertung meine Atemgastproben durch Sibolab. Ich habe die Hinweise zur Testvorbereitung und -durchführung zur Kenntnis genommen.",
      dataUsageAgreement:
        "Ich erkläre mich bereit, dass meine Gesundheitsdaten anonymisiert und pseudonymisiert verwendet werden dürfen, um die Forschung und Entwicklung für neue Dienste und wissenschaftliche Studien durchzuführen (freiwillig).",

      // Buttons
      save: "Speichern",
      submit: "Absenden",
      cancel: "Abbrechen",
      confirm: "Bestätigen",
      close: "Schließen",
      editSubmission: "Angaben bearbeiten",

      // Messages
      thankYou: "Vielen Dank!",
      loadingTestInformation: "Testinformationen werden geladen...",
      formSavedSuccessfully: "Formular erfolgreich gespeichert!",
      formSubmittedSuccessfully: "Formular erfolgreich übermittelt!",
      errorSavingForm:
        "Fehler beim Speichern des Formulars. Bitte versuchen Sie es erneut.",
      errorSubmittingForm:
        "Fehler beim Absenden des Formulars. Bitte versuchen Sie es erneut.",
      draftLoaded: "Entwurf geladen von",
      formDataLoaded: "Formulardaten erfolgreich geladen!",
      errorLoadingForm:
        "Fehler beim Laden der Formulardaten. Bitte versuchen Sie es erneut.",
      loading: "(Wird geladen...)",
      searchMoreRecords: "(Weitere Einträge werden gesucht...)",
      loadingTherapists: "Therapeuten werden geladen...",
      searchAllTherapists: "Alle Therapeuten-Datensätze werden durchsucht...",
      loadingTherapistsSecondary:
        "Bitte warten Sie, während wir die Therapeutenliste abrufen...",

      // Validation
      pleaseEnterFirstName: "Bitte geben Sie den Vornamen ein",
      pleaseEnterLastName: "Bitte geben Sie den Nachnamen ein",
      pleaseEnterDateOfBirth: "Bitte geben Sie das Geburtsdatum ein",
      pleaseSelectTherapist: "Bitte wählen Sie einen Therapeuten aus",
      pleaseAcceptConsent:
        "Bitte akzeptieren Sie die Einverständniserklärung",
      pleaseAcceptDataUsage:
        "Bitte akzeptieren Sie die Vereinbarung zur Datennutzung",
      pleaseFillRequiredFields:
        "Bitte füllen Sie vor dem Absenden alle Pflichtfelder aus.",

      // Instructions Popup
      instructionsTitle: "Richtlinien",
      instructionsContent: "Richtlinien werden hier hinzugefügt.",
      download: "Herunterladen",
      selectLanguage: "Sprache auswählen",
      understood: "Verstanden",
      sampleCollectionVideoTitle: "Video: Probenentnahme",
      // Labels for the video player controls (Plyr `i18n` option)
      sampleCollectionVideoPlayer: {
        play: "Wiedergabe",
        pause: "Pause",
        rewind: "{seektime} s zurück",
        fastForward: "{seektime} s vor",
        seek: "Suchen",
        seekLabel: "{currentTime} von {duration}",
        currentTime: "Aktuelle Zeit",
        settings: "Einstellungen",
        speed: "Geschwindigkeit",
        normal: "Normal",
        enterFullscreen: "Vollbild",
        exitFullscreen: "Vollbild beenden",
        menuBack: "Zurück zum vorherigen Menü",
      },

      // Dialog Messages
      saveDialogTitle: "Formular speichern",
      saveDialogMessage:
        "Möchten Sie das Formular wirklich als Entwurf speichern?",
      submitDialogTitle: "Formular absenden",
      submitInstructions:
        "Ihre Daten werden sicher an uns übertragen und gespeichert und gemäß den geltenden deutschen und EU-Datenschutzgesetzen nicht an unbefugte Dritte weitergegeben.",
      submitDialogMessage:
        "Möchten Sie das Formular wirklich absenden? Diese Aktion kann nicht rückgängig gemacht werden.",
    },

    french: {
      // Header
      informationForm: "Information du patient",
      formDescription:
        "Veuillez remplir toutes les informations requises avec précision. Ce formulaire est utilisé pour recueillir des données essentielles sur le patient en vue de l’analyse de votre test respiratoire SIBO.",

      // Test Information
      testInformation: "Informations obligatoires",
      orderNumber: "Numéro de commande",
      testIdentifier: "Identifiant du test",
      typeOfTest: "Type de test",
      testSubstrate: "Substrat de test",

      // Form Status
      formNotEditable: "Ce formulaire n'est actuellement pas modifiable.",
      loadingFormData: "Chargement des données du formulaire...",

      // Mandatory Information
      mandatoryInformation: "Informations obligatoires",
      firstName: "Prénom",
      lastName: "Nom de famille",
      dateOfBirth: "Date de naissance",
      enterFirstName: "Entrez le prénom",
      enterLastName: "Entrez le nom de famille",

      // Additional Email
      additionalEmailAddress: "Adresse e-mail supplémentaire",
      additionalEmailDescription:
        "Vous pouvez éventuellement ajouter une ou plusieurs adresses e-mail pour recevoir une copie des résultats.",
      enterAdditionalEmail: "Entrez l'adresse e-mail supplémentaire",
      addEmailAddress: "Ajouter une adresse e-mail",
      removeEmail: "Supprimer",

      // Additional Information
      additionalInformation: "Autresinformations",
      initialOrFollowUp: "Test initial ou de suivi",
      initialFindings: "Test initial",
      followUpTest: "Test de suivi",

      // Daily Symptoms
      dailySymptoms: " Plantesau quotidie",
      selectDailySymptoms: "Sélectionnez vos symptômes quotidiens :",
      constipation: "Constipation",
      bloating: "Ballonement abdominal",
      diarrhoea: "Diarrhée",
      abdominalPain: "Douleurs abdominales",
      mixed: "Constipation/Diarrhée en alternance",
      flatulence: "Flatulences",

      // Physician or Therapist
      physicianTherapist: "Médecin traitant ou Thérapeut",
      therapistSelectionDescription:
        "Sélectionnez votre thérapeute ou médecin dans la liste afin qu'une copie supplémentaire de vos résultats lui soit envoyée. Seuls les cabinets enregistrés auprès de Sibolab sont répertoriés. Si votre cabinet ne figure pas dans la liste, veuillez saisir les informations manuellement sous « Autres ».",
      therapistTestNumberNote:
        "Veuillez communiquer votre numéro de test à votre thérapeute afin qu'il puisse accéder à votre rapport de test et le télécharger.",
      searchTherapist: "Rechercher un thérapeute",
      searchTherapistHint:
        "Tapez le nom du thérapeute, le nom du cabinet ou la ville...",
      therapistName: "Nom du thérapeute",
      practiceName: "Nom du cabinet",
      city: "Ville",
      email: "E-mail",
      entertherapistName: "Entrez le nom du thérapeute",
      enterpracticeName: "Entrez le nom du cabinet",
      entercity: "Entrez la ville",
      enteremail: "Entrez l'e-mail",

      otherNotListed: "Autre (non répertorié)",
      enterCustomTherapist:
        "Saisissez les informations du thérapeute personnalisé",
      customTherapistInformation:
        "Informations personnalisées sur le thérapeute",
      customTherapistInfoDescription:
        "Veuillez saisir les informations de votre thérapeute ou médecin.",

      // Test Symptoms
      testSymptoms: "Symptômes lors de l'exécution du test",
      testSymptomsDescription:
        "Veuillez sélectionner les symptômes qui sont apparus pendant le test et indiquer le moment (en minutes) où ils sont survenus.",
      minutes: "Minutes",
      others: "Autres",
      addSymptom: "Ajouter un symptôme",
      removeSymptom: "Supprimer",
      describeOtherSymptoms: "Décrire les autres symptômes (2 à 3 mots)",
      wordsUsed: "mots utilisés",
      maximumSymptoms: "(Maximum 3 symptômes)",
      keepbrief: "Veuillez être bref.",
      toomanywords: "Trop de mots",
      maximumSymptomsReached: "Maximum 3 symptômes autorisés",

      // Declaration
      declaration: "Déclaration",
      consentDeclaration:
        "Par la présente, je donne mon consentement à l'évaluation de mes échantillons de test respiratoire par Sibolab. J'ai pris connaissance des instructions relatives à la préparation et à la réalisation du test.",
      dataUsageAgreement:
        "Je consens à ce que mes données de santé soient anonymisées et pseudonymisées afin d'être utilisées dans le cadre de la recherche et du développement de nouveaux services ainsi que d'études scientifiques (volontaire).",

      // Buttons
      save: "Enregistrer",
      submit: "Soumettre",
      cancel: "Annuler",
      confirm: "Confirmer",
      close: "Fermer",
      editSubmission: "Modifier la soumission",

      // Messages
      thankYou: "Merci !",
      loadingTestInformation: "Chargement des informations du test...",

      formSavedSuccessfully: "Formulaire enregistré avec succès !",
      formSubmittedSuccessfully: "Formulaire soumis avec succès !",
      errorSavingForm:
        "Erreur lors de l'enregistrement du formulaire. Veuillez réessayer.",
      errorSubmittingForm:
        "Erreur lors de la soumission du formulaire. Veuillez réessayer.",
      draftLoaded: "Brouillon chargé depuis",
      formDataLoaded: "Données du formulaire chargées avec succès !",
      errorLoadingForm:
        "Erreur lors du chargement des données du formulaire. Veuillez réessayer.",
      loading: "(Chargement...)",
      searchMoreRecords: "(Recherche d'autres enregistrements...)",
      loadingTherapists: "Chargement des thérapeutes...",
      searchAllTherapists:
        "Recherche de tous les enregistrements des thérapeutes...",
      loadingTherapistsSecondary:
        "Veuillez patienter pendant que nous récupérons la liste des thérapeutes...",

      // Validation
      pleaseEnterFirstName: "Veuillez entrer le prénom",
      pleaseEnterLastName: "Veuillez entrer le nom de famille",
      pleaseEnterDateOfBirth: "Veuillez entrer la date de naissance",
      pleaseSelectTherapist: "Veuillez sélectionner un thérapeute",
      pleaseAcceptConsent: "Veuillez accepter la déclaration de consentement",
      pleaseAcceptDataUsage:
        "Veuillez accepter l'accord d'utilisation des données",
      pleaseFillRequiredFields:
        "Veuillez remplir tous les champs obligatoires avant de soumettre.",

      // Instructions Popup
      instructionsTitle: "Instructions",
      instructionsContent: "Les instructions seront ajoutées ici.",
      download: "Télécharger",
      selectLanguage: "Choisir la langue",
      understood: "Compris",
      sampleCollectionVideoTitle: "Vidéo : prélèvement de l'échantillon",
      // Labels for the video player controls (Plyr `i18n` option)
      sampleCollectionVideoPlayer: {
        play: "Lecture",
        pause: "Pause",
        rewind: "Reculer de {seektime} s",
        fastForward: "Avancer de {seektime} s",
        seek: "Rechercher",
        seekLabel: "{currentTime} sur {duration}",
        currentTime: "Temps actuel",
        settings: "Paramètres",
        speed: "Vitesse",
        normal: "Normale",
        enterFullscreen: "Plein écran",
        exitFullscreen: "Quitter le plein écran",
        menuBack: "Retour au menu précédent",
      },

      // Dialog Messages
      saveDialogTitle: "Enregistrer le formulaire",
      saveDialogMessage:
        "Voulez-vous vraiment enregistrer le formulaire en tant que brouillon ?",
      submitDialogTitle: "Soumettre le formulaire",
      submitInstructions:
        "Vos données nous sont transmises et stockées de manière sécurisée, et ne sont pas partagées avec des tiers non autorisés, conformément aux lois allemandes et européennes applicables en matière de protection des données.",
      submitDialogMessage:
        "Voulez-vous vraiment soumettre le formulaire ? Cette action est irréversible.",
    },

    dutch: {
      // Header
      informationForm: "Patiënteninformatie",
      formDescription:
        "Vul alstublieft alle verplichte informatie nauwkeurig in. Dit formulier wordt gebruikt om essentiële patiëntgegevens te verzamelen voor uw SIBO-ademtestanalyse.",

      // Test Information
      testInformation: "Testinformatie",
      orderNumber: "Ordernummer",
      testIdentifier: "Testidentificatie",
      typeOfTest: "Type test",
      testSubstrate: "Testsubstraat",

      // Form Status
      formNotEditable: "Dit formulier kan momenteel niet worden bewerkt.",
      loadingFormData: "Formuliergegevens worden geladen...",

      // Mandatory Information
      mandatoryInformation: "Verplichte informatie",
      firstName: "Voornaam",
      lastName: "Naam",
      dateOfBirth: "Geboortedatum",
      enterFirstName: "Voer voornaam in",
      enterLastName: "Voer achternaam in",

      // Additional Email
      additionalEmailAddress: "Extra e-mailadres",
      additionalEmailDescription:
        "Voeg optioneel een of meer e-mailadressen toe om een kopie van de resultaten te ontvangen.",
      enterAdditionalEmail: "Voer extra e-mailadres in",
      addEmailAddress: "E-mailadres toevoegen",
      removeEmail: "Verwijderen",

      // Additional Information
      additionalInformation: "Aanvullende informatie",
      initialOrFollowUp: " Eerste test of vervolgtest",
      initialFindings: " Eerste test",
      followUpTest: "Vervolgtest",

      // Daily Symptoms
      dailySymptoms: " Klachten in het dagelijks leven",
      selectDailySymptoms: "Selecteer uw dagelijkse symptomen:",
      constipation: "Constipatie",
      bloating: "Uitgebreide buik",
      diarrhoea: "Diarree",
      abdominalPain: "Buikpijn",
      mixed: "Constipatie/Diarree ombeurten",
      flatulence: "Winderigheid",

      // Physician or Therapist
      physicianTherapist: " Behandelend arts of therapeut",
      therapistSelectionDescription:
        "Selecteer uw therapeut of arts uit de lijst, zodat een extra kopie van uw resultaten naar hen wordt verzonden. In de lijst staan uitsluitend bij Sibolab geregistreerde praktijken. Als uw praktijk niet in de lijst staat, voer dan de gegevens handmatig in onder 'Overige'.",
      therapistTestNumberNote:
        "Geef uw therapeut uw testnummer door, zodat zij uw testrapport kunnen openen en downloaden.",
      searchTherapist: "Zoek therapeut",
      searchTherapistHint: "Typ naam van therapeut, praktijk of stad...",
      therapistName: "Naam van therapeut",
      practiceName: "Praktijknaam",
      city: "Stad",
      email: "E-mail",
      entertherapistName: "Voer de naam van de therapeut in",
      enterpracticeName: "Voer de praktijknaam in",
      entercity: "Voer de stad in",
      enteremail: "Voer het e-mailadres in",

      otherNotListed: "Overige (niet vermeld)",
      enterCustomTherapist: "Voer aangepaste therapeutinformatie in",
      customTherapistInformation: "Aangepaste therapeutinformatie",
      customTherapistInfoDescription:
        "Voer de gegevens van uw therapeut of arts in.",

      // Test Symptoms
      testSymptoms: "Symptomen tijdens de testuitvoering",
      testSymptomsDescription:
        "Selecteer de symptomen die zich tijdens de test hebben voorgedaan en geef aan op welk moment (in minuten) ze optraden.",
      minutes: "Minuten",
      others: "Overige",
      addSymptom: "Symptoom toevoegen",
      removeSymptom: "Verwijderen",
      describeOtherSymptoms: "Beschrijf andere symptomen (2–3 woorden)",
      wordsUsed: "woorden gebruikt",
      maximumSymptoms: "(Maximaal 3 symptomen)",
      keepbrief: "Houd het kort.",
      toomanywords: "Te veel woorden",
      maximumSymptomsReached: "Maximaal 3 symptomen toegestaan",

      // Declaration
      declaration: "Verklaring",
      consentDeclaration:
        "Hierbij verklaar ik mijn toestemming voor de evaluatie van mijn ademtestmonsters door Sibolab. Ik heb kennisgenomen van de instructies voor de voorbereiding en uitvoering van de test.",
      dataUsageAgreement:
        "Ik ga ermee akkoord dat mijn gezondheidsgegevens anoniem en pseudoniem mogen worden gebruikt voor het uitvoeren van onderzoeken, ontwikkeling voor nieuwe diensten en wetenschappelijke studies (op vrijwillige basis)",

      // Buttons
      save: "Opslaan",
      submit: "Verzenden",
      cancel: "Annuleren",
      confirm: "Bevestigen",
      close: "Sluiten",
      editSubmission: "Inzending bewerken",

      // Messages
      thankYou: "Bedankt!",
      loadingTestInformation: "Testinformatie wordt geladen...",
      formSavedSuccessfully: "Formulier succesvol opgeslagen!",
      formSubmittedSuccessfully: "Formulier succesvol verzonden!",
      errorSavingForm:
        "Fout bij het opslaan van het formulier. Probeer het opnieuw.",
      errorSubmittingForm:
        "Fout bij het verzenden van het formulier. Probeer het opnieuw.",
      draftLoaded: "Concept geladen van",
      formDataLoaded: "Formuliergegevens succesvol geladen!",
      errorLoadingForm:
        "Fout bij het laden van de formuliergegevens. Probeer het opnieuw.",
      loading: "(Laden...)",
      searchMoreRecords: "(Meer gegevens worden opgezocht...)",
      loadingTherapists: "Therapeuten worden geladen...",
      searchAllTherapists: "Alle therapeutenrecords worden doorzocht...",
      loadingTherapistsSecondary:
        "Even geduld terwijl we de therapeutenlijst ophalen...",

      // Validation
      pleaseEnterFirstName: "Voer voornaam in",
      pleaseEnterLastName: "Voer achternaam in",
      pleaseEnterDateOfBirth: "Voer geboortedatum in",
      pleaseSelectTherapist: "Selecteer een therapeut",
      pleaseAcceptConsent: "Accepteer de toestemmingsverklaring",
      pleaseAcceptDataUsage: "Accepteer het akkoord voor gegevensgebruik",
      pleaseFillRequiredFields:
        "Vul alle verplichte velden in voordat u het formulier verzendt.",

      // Instructions Popup
      instructionsTitle: "Instructies",
      instructionsContent: "Instructies worden hier toegevoegd.",
      download: "Downloaden",
      selectLanguage: "Taal kiezen",
      understood: "Begrepen",
      sampleCollectionVideoTitle: "Video: monstername",
      // Labels for the video player controls (Plyr `i18n` option)
      sampleCollectionVideoPlayer: {
        play: "Afspelen",
        pause: "Pauzeren",
        rewind: "{seektime} s terug",
        fastForward: "{seektime} s vooruit",
        seek: "Zoeken",
        seekLabel: "{currentTime} van {duration}",
        currentTime: "Huidige tijd",
        settings: "Instellingen",
        speed: "Snelheid",
        normal: "Normaal",
        enterFullscreen: "Volledig scherm",
        exitFullscreen: "Volledig scherm verlaten",
        menuBack: "Terug naar vorig menu",
      },

      // Dialog Messages
      saveDialogTitle: "Formulier opslaan",
      saveDialogMessage:
        "Weet u zeker dat u het formulier als concept wilt opslaan?",
      submitDialogTitle: "Formulier verzenden",
      submitInstructions:
        "Uw gegevens worden naar ons verzonden en veilig opgeslagen en worden niet gedeeld met onbevoegde derden, in overeenstemming met de relevante Duitse en EU-wetgeving inzake gegevensbescherming.",
      submitDialogMessage:
        "Weet u zeker dat u het formulier wilt verzenden? Deze actie kan niet ongedaan worden gemaakt.",
    },
  };

  return translations[language] || translations.english;
};

